import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useDispatch } from "react-redux";
import DataTable from '../../components/Table';
import { toaster } from "../../utils/utils";
import apiConstants from "../../constans/apiConstants";


const ftProcessApiUrl = () =>
  `${apiConstants().PROPOSEL_API_URL}api/v1/proposal/ops/newgen/ft`;

const authHeaders = () => ({
  "Content-Type": "application/json",
  Authorization: localStorage.getItem("token"),
});

const FT_HEADERS = [
  "Proposal Number",
  "FT Type",
  "Old Policy Number",
  "Old Policy Amount",
  "New Proposal Amount",
  "FT Amount",
  "Channel",
  "Sub Channel",
  "Actions",
];

const FT_KEYS = [
  "proposalNumber",
  "ftType",
  "oldPolicyNumber",
  "oldPolicyAmount",
  "newProposalAmount",
  "ftAmount",
  "channel",
  "subChannel",
  "ftRowActions",
];

const FT_SEARCH_KEYS = FT_KEYS.filter((k) => k !== "ftRowActions");

/* -------------------------------------------------------------------------- */
/*  Pure helpers                                                               */
/* -------------------------------------------------------------------------- */

const axiosErrorMessage = (err) =>
  err?.response?.data?.message || err?.data?.message;

const normalizeRow = (row, index) => ({
  ...row,
  ftRowActions: null,
  id: row.id ?? row.ID ?? row.proposalNumber ?? index,
});

const rowMatchesSearch = (row, query) => {
  const q = String(query ?? "").trim().toLowerCase();
  if (!q) return true;
  return FT_SEARCH_KEYS.some((key) => {
    const val = row[key];
    if (val === null || val === undefined || val === "") return false;
    return String(val).toLowerCase().includes(q);
  });
};

const amountCellIsBlank = (v) =>
  v === null ||
  v === undefined ||
  (typeof v === "string" && v.trim() === "");

/** Editable column is Old Policy Amount; FT amount is list-only from API. */
const canEditOldPolicyAmount = (row) => {
  const status = row?.paymentStatusCode;
  if (status === "PA" || status === "PM") {
    return false;
  }
  if (row.oldPolicyAmountModified === false) return true;
  if (row.oldPolicyAmountModified === true) return false;
  return row.ftAmountModified === false;
};

const parseAmount = (raw) => {
  const n = Number(String(raw).replace(/,/g, ""));
  return Number.isNaN(n) ? null : n;
};

/* -------------------------------------------------------------------------- */
/*  Confirm modal (FT-specific UI)                                             */
/* -------------------------------------------------------------------------- */

function OldPolicyAmountConfirmModal({ onClose, onConfirm, submitting }) {
  const busy = Boolean(submitting);
  return (
    <div
      className="ft-confirm-modal-root"
      role="dialog"
      aria-modal="true"
      aria-busy={busy}
      aria-labelledby="ft-confirm-modal-title"
    >
      <button
        type="button"
        className="ft-confirm-modal-backdrop"
        aria-label="Close dialog"
        disabled={busy}
        onClick={() => {
          if (!busy) onClose();
        }}
      />
      <div className="ft-confirm-modal-panel">
        <div className="ft-confirm-modal-header">
          <h2 id="ft-confirm-modal-title">Update Old Policy amount</h2>
          <button
            type="button"
            className="ft-confirm-modal-close"
            aria-label="Close"
            disabled={busy}
            onClick={onClose}
          >
            &times;
          </button>
        </div>
        <div className="ft-confirm-modal-body">
          <p>Are you sure you want to update the Old Policy amount?</p>
        </div>
        <div className="ft-confirm-modal-footer">
          <button
            type="button"
            className="ft-confirm-btn ft-confirm-btn--secondary"
            disabled={busy}
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="button"
            className="ft-confirm-btn ft-confirm-btn--primary"
            disabled={busy}
            onClick={() => onConfirm()}
          >
            {busy ? "Saving…" : "OK"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Page                                                                       */
/* -------------------------------------------------------------------------- */

const FtProcess = () => {
  const dispatch = useDispatch();
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [draftById, setDraftById] = useState({});
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmSubmitting, setConfirmSubmitting] = useState(false);
  const [pendingSave, setPendingSave] = useState(null);

  /* Full list in one GET (same idea as ProposalDashboard → selectedProposals); no server-side page/size. */
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      dispatch({ type: "LOADER_TRUE" });
      try {
        const res = await axios.get(ftProcessApiUrl(), {
          headers: authHeaders(),
        });
        if (cancelled) return;
        dispatch({ type: "LOADER_FALSE" });
        const body = res?.data?.body;
        const list = Array.isArray(body) ? body.map(normalizeRow) : [];
        setRows(list);
        if (res?.data?.message) toaster("success", res.data.message);
      } catch (err) {
        if (cancelled) return;
        dispatch({ type: "LOADER_FALSE" });
        setRows([]);
        const msg = axiosErrorMessage(err);
        if (msg) toaster("error", msg);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [dispatch]);

  /* Same pattern as ProposalDashboard: one array for data + paginationItems (search only narrows client-side). */
  const ftTableData = useMemo(() => {
    const q = search.trim();
    if (!q) return rows;
    return rows.filter((r) => rowMatchesSearch(r, q));
  }, [search, rows]);

  /* Search changed: exit edit (pagination lives inside DataTable) */
  useEffect(() => {
    setEditingId(null);
    setDraftById({});
  }, [search]);

  /* Focus amount input after Edit */
  useEffect(() => {
    if (editingId == null) return;
    const t = window.setTimeout(() => {
      const el = document.querySelector(
        `[data-old-policy-amount-input="${String(editingId)}"]`
      );
      if (el) {
        if (typeof el.focus === "function") el.focus();
        if (typeof el.select === "function") el.select();
      }
    }, 0);
    return () => window.clearTimeout(t);
  }, [editingId]);

  const exportRows = useCallback(async (forExcel) => {
    if (forExcel) {
      return ftTableData.map(({ ftRowActions, ...rest }) => rest);
    }
    return undefined;
  }, [ftTableData]);

  const stopEdit = useCallback((rowId) => {
    setEditingId(null);
    setDraftById((prev) => {
      const next = { ...prev };
      delete next[rowId];
      return next;
    });
  }, []);

  const closeModal = useCallback(() => {
    setConfirmOpen(false);
    setPendingSave(null);
    setConfirmSubmitting(false);
  }, []);

  const startEdit = useCallback(
    (row) => {
      if (!canEditOldPolicyAmount(row)) return;
      if (editingId != null && editingId !== row.id) return;
      setEditingId(row.id);
      setDraftById((prev) => ({
        ...prev,
        [row.id]:
          row.oldPolicyAmount == null ? "" : String(row.oldPolicyAmount),
      }));
    },
    [editingId]
  );

  const submitPatch = useCallback(async () => {
    if (!pendingSave) return;
    const { id, rowId, oldPolicyAmount } = pendingSave;
    /* Avoid global #cover-spin here — it sits above the modal (white flash on close). */
    setConfirmSubmitting(true);
    try {
      const res = await axios.patch(
        ftProcessApiUrl(),
        { id, oldPolicyAmount },
        { headers: authHeaders() }
      );
      setRows((prev) =>
        prev.map((r) =>
          r.id === rowId
            ? {
                ...r,
                oldPolicyAmount,
                oldPolicyAmountModified: true,
                ftAmountModified: true,
              }
            : r
        )
      );
      stopEdit(rowId);
      /* Let row update paint before removing overlay (smoother than sync unmount). */
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          closeModal();
        });
      });
      if (res?.data?.message) toaster("success", res.data.message);
    } catch (err) {
      const msg = axiosErrorMessage(err);
      if (msg) toaster("error", msg);
      setConfirmSubmitting(false);
      closeModal();
    }
  }, [pendingSave, stopEdit, closeModal]);

  const openConfirm = useCallback(
    (row) => {
      if (editingId !== row.id) return;
      const raw = draftById[row.id];
      if (raw === undefined || String(raw).trim() === "") {
        stopEdit(row.id);
        return;
      }
      const num = parseAmount(raw);
      if (num == null) {
        toaster("error", "Old Policy amount must be a valid number.");
        return;
      }
      setPendingSave({
        id: row.id,
        rowId: row.id,
        oldPolicyAmount: String(num),
      });
      setConfirmOpen(true);
    },
    [editingId, draftById, stopEdit]
  );

  const renderCell = useCallback(
    (item, key) => {
      if (key === "oldPolicyAmount") {
        if (editingId === item.id) {
          return (
            <div className="ft-amount-edit-cell">
              <input
                type="text"
                inputMode="decimal"
                className="ft-filter-select"
                data-old-policy-amount-input={item.id}
                value={draftById[item.id] ?? ""}
                onChange={(e) => {
                  const v = e.target.value.replace(/[^\d.,]/g, "");
                  setDraftById((p) => ({ ...p, [item.id]: v }));
                }}
              />
              <button
                type="button"
                className="cancel-btn"
                aria-label="Cancel editing"
                title="Cancel editing"
                onClick={() => stopEdit(item.id)}
              >
                &times;
              </button>
            </div>
          );
        }
        if (amountCellIsBlank(item.oldPolicyAmount)) {
          return <span className="text-muted">—</span>;
        }
        return item.oldPolicyAmount;
      }

      if (key === "ftRowActions") {
        const isThisRow = editingId === item.id;
        const otherRowEditing = editingId != null && editingId !== item.id;
        return (
          <div className="dataTable-btn" style={{ whiteSpace: "nowrap" }}>
            <button
              type="button"
              className="edit-btn"
              disabled={
                !canEditOldPolicyAmount(item) || otherRowEditing || isThisRow
              }
              onClick={() => startEdit(item)}
            >
              Edit
            </button>{" "}
            <button
              type="button"
              className="save-btn"
              disabled={!isThisRow}
              onClick={() => openConfirm(item)}
            >
              Save
            </button>
          </div>
        );
      }

      return null;
    },
    [editingId, draftById, startEdit, openConfirm, stopEdit]
  );

  const searchTrimmed = search.trim();
  const noMatches =
    rows.length > 0 && ftTableData.length === 0 && searchTrimmed !== "";

  return (
    <div className="upfront-payment-report">
      <h1>FT Process Report</h1>

      <div className="filters">
        <div className="comp1">
          <div className="filter-component">
            <div className="filter-options">
              <div className="filter">
                <label>Search:</label>
                <input
                  type="text"
                  className="filter-select"
                  value={search}
                  placeholder="Please Enter"
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {noMatches && (
        <p className="text-muted" style={{ marginTop: "12px" }}>
          No rows match your search.
        </p>
      )}

      {ftTableData.length > 0 && (
        <div className="reconciliation-table-wrapper ft-process-table-wrapper">
          <DataTable
            cellHeaders={FT_HEADERS}
            keys={FT_KEYS}
            data={ftTableData}
            fileName="FT-Process-Report"
            addSerialNo={true}
            showExcelDownload={true}
            PerPageApiData={false}
            paginationItems={ftTableData}
            alwaysShown={false}
            callUpfrontPaymentReportApi={exportRows}
            converTdToComponent="oldPolicyAmount"
            secondconverTdToComponent="ftRowActions"
            componentToBeConverted={renderCell}
          />
        </div>
      )}

      {confirmOpen && (
        <OldPolicyAmountConfirmModal
          onClose={closeModal}
          onConfirm={submitPatch}
          submitting={confirmSubmitting}
        />
      )}
    </div>
  );
};

export default FtProcess;
